import { alpha, type Theme } from '@mui/material/styles';

/**
 * Built-in tooltip looks. Add a new key here (and an entry in
 * {@link getTooltipVariantTokens}) to introduce another variant — every
 * consumer of {@link TooltipCard} can then opt into it through the `variant`
 * prop without touching the component itself.
 *
 * - `default` — neutral themed panel (the login completion nudge look).
 * - `warning` — soft amber attention hint (the "click Edit to start" /
 *   "start a new version" prompts).
 * - `info`    — light info surface, paired with an info icon.
 * - `dark`    — high-contrast near-black label (the small hover hints).
 * - `neutral` — plain mid-grey label matching MUI's classic default tooltip.
 */
export type TooltipVariant = 'default' | 'warning' | 'info' | 'dark' | 'neutral';

export interface TooltipVariantTokens {
  /** Surface background. */
  backgroundColor: string;
  /** Foreground / text colour. */
  color: string;
  /** Arrow colour — matches the surface for a seamless join. */
  arrowColor: string;
  /** Optional 1px border colour. */
  borderColor?: string;
  /** Drop shadow. */
  boxShadow: string;
  /** Accent used for leading icons, progress, etc. */
  accentColor: string;
}

/**
 * Resolves every tooltip variant to concrete colours pulled from the active
 * MUI theme — never hard-coded hex — so the tooltips stay consistent with the
 * rest of the application and follow light/dark themes automatically.
 */
export const getTooltipVariantTokens = (
  theme: Theme,
): Record<TooltipVariant, TooltipVariantTokens> => ({
  default: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    arrowColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    boxShadow: `0 8px 28px ${alpha(theme.palette.common.black, 0.14)}`,
    accentColor: theme.palette.primary.main,
  },
  warning: {
    // Soft, warm warning surface: a light amber tint with deep-amber text and a
    // gentle border — all from the theme's yellow scale + `warning.main` accent.
    backgroundColor: theme.palette.yellow[200],
    color: theme.palette.yellow[1000],
    arrowColor: theme.palette.yellow[200],
    borderColor: theme.palette.yellow[400],
    boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.18)}`,
    accentColor: theme.palette.warning.main,
  },
  info: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    arrowColor: theme.palette.background.paper,
    borderColor: theme.palette.black[100],
    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
    accentColor: theme.palette.primary.main,
  },
  dark: {
    // `black[1400]` (#0A0A0A) is theme-independent, so the high-contrast label
    // stays a dark surface with white text in both light and dark themes —
    // unlike `text.primary`, which flips to white in dark mode.
    backgroundColor: theme.palette.black[1400],
    color: theme.palette.common.white,
    arrowColor: theme.palette.black[1400],
    boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.28)}`,
    accentColor: theme.palette.common.white,
  },
  neutral: {
    // Reproduces MUI's classic default tooltip — a plain, soft mid-grey label —
    // using the theme-independent `black` scale so it's identical in both themes.
    backgroundColor: alpha(theme.palette.black[600], 0.92),
    color: theme.palette.common.white,
    arrowColor: alpha(theme.palette.black[600], 0.92),
    boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.2)}`,
    accentColor: theme.palette.common.white,
  },
});

/** Padding / typography presets keyed by the `size` prop. */
export type TooltipSize = 'sm' | 'md' | 'lg';

export interface TooltipSizeTokens {
  padding: string;
  fontSize: string;
  borderRadius: string;
  gap: number;
  defaultMaxWidth: number;
}

export const TOOLTIP_SIZE_TOKENS: Record<TooltipSize, TooltipSizeTokens> = {
  sm: { padding: '6px 10px', fontSize: '0.8125rem', borderRadius: '8px', gap: 0.75, defaultMaxWidth: 220 },
  md: { padding: '10px 14px', fontSize: '0.875rem', borderRadius: '10px', gap: 1, defaultMaxWidth: 300 },
  lg: { padding: '16px', fontSize: '0.9375rem', borderRadius: '12px', gap: 1.25, defaultMaxWidth: 360 },
};
